import React, { FC, useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, Tooltip, XAxis, YAxis } from 'recharts';
import StableResponsiveContainer from '../general/stable-responsive-container/stable-responsive-container.component';
import { StatsResponse } from '../../models/stats.models';
import { formatDurationHHmm, getMeasurementUnit } from '../../helpers/general.helpers';
import { getFocusColor, getFocusLabel } from '../../pages/training-log/training-log.helpers';
import {
  buildFocusDonutData,
  buildPeriodFocusPercentData,
  FocusDonutItem,
  formatDistributionPercent,
  formatDistributionRawValue,
  getFocusKeysForPeriodLegend,
  hasFocusData,
  MetricMode,
  PeriodFocusChartRow,
} from './focus-breakdown.helpers';
import './focus-breakdown.component.css';

interface FocusBreakdownProps {
  stats: StatsResponse | null;
  loading: boolean;
}

interface FocusDonutChartProps {
  data: FocusDonutItem[];
  mode: MetricMode;
  distanceUnit: string;
}

const FocusDonutChart: FC<FocusDonutChartProps> = ({ data, mode, distanceUnit }) => {
  if (data.every((item) => item.rawValue === 0)) {
    return <div className="fb-chart-empty">No focus data</div>;
  }

  return (
    <div className="fb-distribution">
      <div className="fb-donut-container">
        <StableResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={54} outerRadius={81}>
              {data.map((entry) => (
                <Cell key={entry.key} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </StableResponsiveContainer>
      </div>
      <div className="fb-donut-legend">
        {data.map((item) => (
          <div className="fb-donut-legend-row" key={item.key}>
            <div className="fb-donut-legend-dot" style={{ background: item.color }} />
            <div>
              <div className="fb-donut-legend-label">{item.name}</div>
              <div className="fb-donut-legend-detail">
                {formatDistributionPercent(item.percent)} ·{' '}
                {formatDistributionRawValue(item.rawValue, mode, distanceUnit)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface FocusLegendProps {
  focusKeys: string[];
}

const FocusLegend: FC<FocusLegendProps> = ({ focusKeys }) => (
  <div className="fb-focus-legend">
    {focusKeys.map((focusKey, index) => (
      <div className="fb-focus-legend-item" key={focusKey}>
        <div className="fb-focus-legend-dot" style={{ background: getFocusColor(focusKey, index) }} />
        {getFocusLabel(focusKey)}
      </div>
    ))}
  </div>
);

interface FocusPeriodPercentChartProps {
  data: PeriodFocusChartRow[];
  focusKeys: string[];
}

const FocusPeriodPercentChart: FC<FocusPeriodPercentChartProps> = ({ data, focusKeys }) => {
  if (data.length === 0 || focusKeys.length === 0) {
    return <div className="fb-chart-empty">No period focus data</div>;
  }

  return (
    <div className="fb-chart-container" style={{ height: 240 }}>
      <StableResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
          barCategoryGap="20%"
          stackOffset="expand"
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#6B7280' }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
            interval="preserveStartEnd"
            minTickGap={16}
          />
          <YAxis
            domain={[0, 1]}
            tick={{ fontSize: 10, fill: '#6B7280' }}
            tickLine={false}
            axisLine={false}
            width={48}
            tickFormatter={(value) => `${Math.round(value * 100)}%`}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) {
                return null;
              }

              const totalDuration = payload.reduce((sum, entry) => sum + Number(entry.value ?? 0), 0);

              return (
                <div
                  style={{
                    background: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: 8,
                    padding: '8px 12px',
                    fontSize: 12,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 6, color: '#111827' }}>{label}</div>
                  {payload
                    .filter((entry) => Number(entry.value) > 0)
                    .map((entry) => {
                      const focusKey = String(entry.dataKey);
                      const durationSeconds = Number(entry.value);
                      const percent = totalDuration > 0 ? (durationSeconds / totalDuration) * 100 : 0;

                      return (
                        <div key={focusKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 2,
                              background: entry.color,
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ color: '#6B7280', minWidth: 72 }}>{getFocusLabel(focusKey)}</span>
                          <span style={{ fontWeight: 600, color: '#111827' }}>
                            {formatDistributionPercent(percent)} · {formatDurationHHmm(durationSeconds)}
                          </span>
                        </div>
                      );
                    })}
                </div>
              );
            }}
          />
          {focusKeys.map((focusKey, index) => (
            <Bar
              key={focusKey}
              dataKey={focusKey}
              stackId="focus"
              fill={getFocusColor(focusKey, index)}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </StableResponsiveContainer>
    </div>
  );
};

const FocusBreakdown: FC<FocusBreakdownProps> = ({ stats, loading }) => {
  const distanceUnit = getMeasurementUnit();

  const durationDonutData = useMemo(
    () => buildFocusDonutData(stats?.focusBreakdown, 'duration'),
    [stats?.focusBreakdown]
  );

  const distanceDonutData = useMemo(
    () => buildFocusDonutData(stats?.focusBreakdown, 'distance'),
    [stats?.focusBreakdown]
  );

  const focusKeys = useMemo(
    () => getFocusKeysForPeriodLegend(stats?.focusBreakdown, stats?.periodBreakdown ?? []),
    [stats?.focusBreakdown, stats?.periodBreakdown]
  );

  const periodData = useMemo(
    () => buildPeriodFocusPercentData(stats?.periodBreakdown ?? [], focusKeys),
    [stats?.periodBreakdown, focusKeys]
  );

  if (loading) {
    return (
      <div className="fb-card">
        <div className="fb-header">
          <div className="fb-title">Training Focus Breakdown</div>
          <div className="fb-subtitle">How training time is distributed across focus categories</div>
        </div>
        <div className="fb-loading">
          <div className="fb-skeleton fb-skeleton--donut" />
          <div className="fb-skeleton fb-skeleton--donut" />
          <div className="fb-skeleton fb-skeleton--chart" />
        </div>
      </div>
    );
  }

  if (!hasFocusData(stats)) {
    return (
      <div className="fb-card">
        <div className="fb-header">
          <div className="fb-title">Training Focus Breakdown</div>
          <div className="fb-subtitle">How training time is distributed across focus categories</div>
        </div>
        <div className="fb-empty">No focus data for this season</div>
      </div>
    );
  }

  return (
    <div className="fb-card">
      <div className="fb-header">
        <div className="fb-title">Training Focus Breakdown</div>
        <div className="fb-subtitle">How training time is distributed across focus categories</div>
      </div>

      <div className="fb-grid">
        <div className="fb-quadrant fb-quadrant--border-right fb-quadrant--border-bottom">
          <div className="fb-section-title">Duration by Focus</div>
          <div className="fb-section-subtitle">Total hours per focus type</div>
          <FocusDonutChart data={durationDonutData} mode="duration" distanceUnit={distanceUnit} />
        </div>

        <div className="fb-quadrant fb-quadrant--border-bottom">
          <div className="fb-section-title">Distance by Focus</div>
          <div className="fb-section-subtitle">Total km per focus type</div>
          <FocusDonutChart data={distanceDonutData} mode="distance" distanceUnit={distanceUnit} />
        </div>

        <div className="fb-quadrant fb-quadrant--full">
          <div className="fb-section-title">Focus % by Period</div>
          <div className="fb-section-subtitle">Percentage of training time per focus per mesocycle</div>
          <FocusLegend focusKeys={focusKeys} />
          <FocusPeriodPercentChart data={periodData} focusKeys={focusKeys} />
        </div>
      </div>
    </div>
  );
};

export default FocusBreakdown;
